// src/modules/app-views/crm/pages/Boards/modals/BoardSettingsModal/tabs/FieldEditorModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState } from 'react';
import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import { getFieldTypeOptions, getFieldTypeMeta } from '../../../../../constants/formFieldTypes';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const FieldEditorModal = ({ open, onClose, field, onSave, columns, existingFieldNames }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isEditing = !!field;
  const fieldTypeOptions = getFieldTypeOptions();
  const columnOptions = (columns || []).map(c => ({ value: c.uid, label: c.title }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [selectedType, setSelectedType] = useState(field?.fieldType || 'text');
  const [options, setOptions] = useState(field?.options || []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;

    if (field) {
      form.setFieldsValue({
        fieldName: field.fieldName,
        title: field.title,
        fieldType: field.fieldType || 'text',
        placeholder: field.placeholder || '',
        min: field.min ?? null,
        max: field.max ?? null,
        decimalPlaces: field.decimalPlaces ?? 0,
        rows: field.rows ?? 3,
        requiredInColumns: field.requiredInColumns || [],
      });
      setSelectedType(field.fieldType || 'text');
      setOptions(field.options || []);
    } else {
      form.resetFields();
      form.setFieldsValue({ fieldType: 'text', decimalPlaces: 0, rows: 3, requiredInColumns: [] });
      setSelectedType('text');
      setOptions([]);
    }
  }, [open, field, form]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const meta = getFieldTypeMeta(values.fieldType);

      const fieldObj = {
        fieldName: values.fieldName,
        title: values.title,
        fieldType: values.fieldType,
        placeholder: values.placeholder || '',
        requiredInColumns: values.requiredInColumns || [],
        // Type-specific config
        options: meta.hasOptions ? options : [],
        min: values.fieldType === 'number' ? values.min : null,
        max: values.fieldType === 'number' ? values.max : null,
        decimalPlaces: values.fieldType === 'number' ? (values.decimalPlaces ?? 0) : 0,
        rows: values.fieldType === 'textarea' ? (values.rows ?? 3) : 3,
        // Preserve order from existing field
        order: field?.order || 0,
      };

      onSave(fieldObj);
      onClose();
    } catch {
      // Validation error — form shows inline messages
    }
  };

  const handleAddOption = () => {
    setOptions(prev => [...prev, { value: '', label: '' }]);
  };

  const handleRemoveOption = idx => {
    setOptions(prev => prev.filter((_, i) => i !== idx));
  };

  const handleOptionChange = (idx, key, val) => {
    setOptions(prev => prev.map((opt, i) => (i === idx ? { ...opt, [key]: val } : opt)));
  };

  const handleTypeChange = type => {
    setSelectedType(type);
    form.setFieldsValue({ fieldType: type });
  };

  const handleAfterClose = () => {
    form.resetFields();
    setOptions([]);
    setSelectedType('text');
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const meta = getFieldTypeMeta(selectedType);

  // Names already used by other fields (exclude current field when editing)
  const takenNames = (existingFieldNames || []).filter(
    n => !isEditing || n !== field?.fieldName
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Modal
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SettingOutlined />
          {translateX(isEditing ? 'edit_field' : 'new_field')}
        </span>
      }
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      afterClose={handleAfterClose}
      afterOpenChange={visible => {
        if (!visible) return;
        const target = isEditing ? 'title' : 'fieldName';
        form.getFieldInstance(target)?.focus();
      }}
      okText={translateX('confirm')}
      cancelText={translateX('cancel')}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" className="mt-3">
        {/* Field Name — machine-readable identifier */}
        <Form.Item
          name="fieldName"
          label={translateX('field_name')}
          rules={[
            { required: true, message: () => translateX('required_field') },
            {
              pattern: /^[a-zA-Z_][a-zA-Z0-9_]*$/,
              message: () => translateX('field_name_invalid'),
            },
            {
              validator: (_, value) =>
                takenNames.includes(value)
                  ? Promise.reject(translateX('field_name_duplicated'))
                  : Promise.resolve(),
            },
          ]}
          tooltip={translateX('field_name_tooltip')}
        >
          <Input
            placeholder="ex: ano_modelo"
            disabled={isEditing}
            onChange={e => {
              // Force lowercase, replace spaces with underscore
              const val = e.target.value.toLowerCase().replace(/\s+/g, '_');
              form.setFieldsValue({ fieldName: val });
            }}
          />
        </Form.Item>

        {/* Title — display name shown to the consultant on the form */}
        <Form.Item
          name="title"
          label={translateX('title')}
          rules={[{ required: true, message: () => translateX('required_field') }]}
          help={translateX('field_title_help')}
        >
          <Input placeholder={translateX('field_title_placeholder')} />
        </Form.Item>

        {/* Field Type */}
        <Form.Item
          name="fieldType"
          label={translateX('field_type')}
          rules={[{ required: true, message: () => translateX('required_field') }]}
        >
          <Select
            options={fieldTypeOptions}
            onChange={handleTypeChange}
            optionRender={option => {
              const m = getFieldTypeMeta(option.value);
              const Icon = m.icon;
              return (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Tag color={m.color} style={{ margin: 0 }}>
                    <Icon />
                  </Tag>
                  {option.label}
                </span>
              );
            }}
          />
        </Form.Item>

        {/* Options — for select/multiselect/radio */}
        {meta.hasOptions && (
          <Form.Item label={translateX('options')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {options.map((opt, idx) => (
                <Row key={idx} gutter={8} align="middle">
                  <Col flex="1">
                    <Input
                      size="small"
                      placeholder={`${translateX('option')} ${idx + 1}`}
                      value={opt.label}
                      onChange={e => {
                        const label = e.target.value;
                        // Auto-generate value from label (snake_case)
                        const value = label
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/[\u0300-\u036f]/g, '')
                          .replace(/\s+/g, '_')
                          .replace(/[^a-z0-9_]/g, '');
                        handleOptionChange(idx, 'label', label);
                        handleOptionChange(idx, 'value', value);
                      }}
                    />
                  </Col>
                  <Col>
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveOption(idx)}
                    />
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                size="small"
                icon={<PlusOutlined />}
                onClick={handleAddOption}
              >
                {translateX('add_option')}
              </Button>
            </div>
          </Form.Item>
        )}

        {/* Number config */}
        {selectedType === 'number' && (
          <Row gutter={8}>
            <Col span={8}>
              <Form.Item name="min" label={translateX('min')}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max" label={translateX('max')}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="decimalPlaces" label={translateX('decimals')}>
                <InputNumber min={0} max={4} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Textarea config */}
        {selectedType === 'textarea' && (
          <Form.Item name="rows" label={translateX('rows')}>
            <InputNumber min={1} max={20} style={{ width: 120 }} />
          </Form.Item>
        )}

        {/* Placeholder */}
        <Form.Item name="placeholder" label="Placeholder">
          <Input placeholder={translateX('placeholder')} />
        </Form.Item>

        {/* Required in columns */}
        <Form.Item
          name="requiredInColumns"
          label={translateX('required_in_columns')}
          tooltip={translateX('required_in_columns_tooltip')}
        >
          <Select
            mode="multiple"
            options={columnOptions}
            placeholder={translateX('select_columns')}
            allowClear
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default FieldEditorModal;
